// 本月共同時間（W58.6 · ADR-0212）—— **一張卡 + 一份列表**。
//
// W58.5 的時候它是一個**整寬的 `<section>`**，面板自己帶展開狀態，整個面板就是
// 一行標題 + 一份列表。W58.6 把它變成雙欄狀態列的**左欄**，所以它拆成兩個元件：
//
//   - `SharedTimeCard` —— 左欄那張 44px 的卡（點它展開）
//   - `SharedTimeList` —— 展開後的列表，**佔滿整行寬度**
//
// **列表為什麼不在左欄裡面。** 375px 下扣掉 `px-s-4` 可用寬 343px，兩欄各
// `(343 - 8) / 2 = 167.5px`；而列表一行要放「日期區間 62px + 圖示 14px + 標題 +
// N 天」—— 標題只剩約 67px。半寬放不下，所以列表在**狀態列下面**、佔滿寬度。
//
// **面板仍然是共同時間唯一的表面**（DESIGN §5.2）：月曆格**不染色**。這條沒有
// 因為版面重排而改變 —— 測試仍然斷言這個元件的 class 裡沒有 `bg-shared/`。

import { ChevronDown, Users } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { CATEGORY_ICON } from "@/components/calendar/calendarIcons";
import type { SharedSegment } from "@/lib/api/calendar";

export interface SharedTimeCardProps {
  segments: SharedSegment[];
  /** 列表是否展開。狀態住在狀態列（它決定列表畫在哪裡）。 */
  open: boolean;
  onToggle: () => void;
}

/**
 * The left-hand card.
 *
 * **沒有共同空檔時它仍佔位，但不是按鈕。** 兩欄並排的前提是**兩欄等寬** ——
 * 左欄消失的話，右欄會被拉到整行寬度，版面立刻歪掉。所以空狀態是一張**同樣
 * 44px 高、同樣外框**的卡，只是不能點（沒有東西可展開）。
 *
 * **文案是「本月沒有共同時間」，不是「沒有共同的空檔」**（ADR-0215）。
 * 「空檔」說的是**兩個人都有空**，而這個功能的**資料**定義是「**兩個人都有
 * 安排**」（後端 `shared_days` = `me & partner`）—— 兩個相反的東西。舊的
 * 空狀態文案站錯邊，會在一個其實沒問題的月份（你們沒有撞期）說成是一個缺陷。
 * 「本月沒有共同時間」用的是面板自己的名字，兩種讀法下都不會說錯。
 */
export function SharedTimeCard({ segments, open, onToggle }: SharedTimeCardProps) {
  if (segments.length === 0) {
    return (
      <div
        className="flex h-11 w-full items-center gap-s-2 rounded-md border border-hairline bg-paper-deep px-s-3"
        data-testid="calendar-shared-panel-empty"
      >
        <Users size={14} className="shrink-0 text-ink-3" aria-hidden="true" />
        <span className="truncate whitespace-nowrap text-meta text-ink-3">
          本月沒有共同時間
        </span>
      </div>
    );
  }

  return (
    <button
      type="button"
      className="press tap-target flex h-11 w-full items-center gap-s-2 rounded-md border border-hairline bg-paper-deep px-s-3 text-left hover:bg-black/5"
      onClick={onToggle}
      aria-expanded={open}
      data-testid="calendar-shared-toggle"
    >
      <Users size={14} className="shrink-0 text-shared-strong" aria-hidden="true" />
      <span className="flex-1 truncate whitespace-nowrap text-meta font-medium text-ink-1">
        共同時間 · {segments.length} 段
      </span>
      <ChevronDown
        size={14}
        aria-hidden="true"
        className={cn(
          "shrink-0 text-ink-3 transition-transform duration-base",
          open && "rotate-180",
        )}
      />
    </button>
  );
}

export interface SharedTimeListProps {
  segments: SharedSegment[];
  /** Tapping a segment opens the day sheet on its first day. */
  onOpenSegment: (segment: SharedSegment) => void;
}

/**
 * The expanded list — **by contiguous segment, not by day**（DESIGN §5.1）.
 *
 * A month with 12 shared days would otherwise make this panel the new noise
 * source — the exact opposite of what Q3 is trying to achieve.
 */
export function SharedTimeList({ segments, onOpenSegment }: SharedTimeListProps) {
  return (
    <ul
      className="flex flex-col overflow-hidden rounded-md border border-hairline bg-paper-deep"
      data-testid="calendar-shared-list"
    >
      {segments.map((seg) => {
        const Icon = CATEGORY_ICON[seg.category as keyof typeof CATEGORY_ICON];
        return (
          <li
            key={`${seg.start_date}-${seg.end_date}`}
            className="border-b border-hairline last:border-b-0"
          >
            <button
              type="button"
              className="flex w-full items-center gap-s-3 p-s-3 text-left hover:bg-black/5"
              onClick={() => onOpenSegment(seg)}
              data-testid="calendar-shared-segment"
            >
              <span className="w-[62px] shrink-0 text-micro font-semibold tabular-nums text-ink-2">
                {formatRange(seg.start_date, seg.end_date)}
              </span>
              {Icon ? (
                <Icon size={14} strokeWidth={2} className="shrink-0 text-ink-2" aria-hidden="true" />
              ) : null}
              <span className="flex-1 truncate text-body-sm text-ink-1">{seg.title}</span>
              <span className="shrink-0 text-meta text-ink-3">{seg.days} 天</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function formatRange(start: string, end: string): string {
  const s = `${Number(start.slice(5, 7))}/${Number(start.slice(8, 10))}`;
  const e = `${Number(end.slice(5, 7))}/${Number(end.slice(8, 10))}`;
  return s === e ? s : `${s}–${e}`;
}
